import React from "react";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { triggerHaptic } from "@/utilities/haptics";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useRemoveSemLeg } from "@/features/betslip/hooks/useBetslipActions";
import type { SemSelection } from "@/features/betslip/types";
import { isSemSrm } from "@/features/betslip/utils/typeGuards";
import {
  sortSemLegs,
  toOutcomeType,
} from "@/features/betslip/utils/selectionHelpers";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { ComboGroup } from "@/features/betslip/components/parts/ComboGroup";
import { OddsDisplay } from "@/features/betslip/components/parts/OddsDisplay";
import { SemLegAlert } from "@/features/betslip/components/parts/SemLegAlert";

type SemSubCardProps = {
  sem: SemSelection;
  radio?: React.ReactNode;
  /** Whether this SEM is the leg picked into a multi with a leg choice —
   *  drives the group's selected border. Legs stay visible either way. */
  isSelected?: boolean;
};

export const SemSubCard = ({
  sem,
  radio,
  isSelected = false,
}: SemSubCardProps) => {
  const isSrm = isSemSrm(sem);
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const pendingOdds = useBetslipStore((s) => s.oddsChanges[sem.outcomeId]);
  const isLiveViewOnly = useBetslipStore((s) => s.liveViewOnly[sem.outcomeId]);
  // Once the multi is placed, show the price the server took for this SEM.
  const acceptedOdds = useBetslipStore((s) =>
    s.multiResult?.status === "accepted"
      ? s.multiResult.oddsByLeg?.[sem.outcomeId]
      : undefined,
  );
  const removeSemLeg = useRemoveSemLeg();

  const legs = sortSemLegs(sem.legs ?? [], isSrm);
  const legCount = legs.length;
  const canRemoveLeg = legCount > 2;

  const handleRemoveLeg = (legOutcomeId: string) => {
    triggerHaptic.click();
    removeSemLeg(sem.outcomeId, legOutcomeId);
  };

  return (
    <ComboGroup
      variant={isSrm ? "srm" : "sgm"}
      label={`${legCount} Legs`}
      boldLabel={
        <>
          @{" "}
          <OddsDisplay
            odds={acceptedOdds ?? sem.odds}
            newOdds={pendingOdds}
            dash={isLiveViewOnly}
          />
        </>
      }
      action={radio}
      selected={isSelected}
      testId={`sem-sub-${sem.outcomeId}`}
    >
      <BetSpine padded>
        {legs.map((leg) => {
          const competitor = sem.competitors?.[leg.outcomeId];
          // SEM legs show only their market — the combined price lives in the
          // header ("X Legs @ odds"), not per leg.
          const marketLabel = leg.marketName;
          return (
            <React.Fragment key={`${leg.marketId}-${leg.outcomeId}`}>
              <SemLegAlert
                semOutcomeId={sem.outcomeId}
                legOutcomeId={leg.outcomeId}
              />
              <BetRow
                icon={
                  <OutcomeIcon
                    outcomeType={toOutcomeType(leg.outcomeType)}
                    competitor={competitor}
                    silksUrl={sem.silksUrl}
                    size={24}
                    reserveAvatarSpace
                  />
                }
                name={leg.name}
                market={marketLabel}
                rightSlot={
                  canRemoveLeg ? (
                    <RemoveButton
                      onClick={() => handleRemoveLeg(leg.outcomeId)}
                      disabled={isPlacing}
                      testId={`remove-sem-leg-${leg.outcomeId}`}
                    />
                  ) : null
                }
                testId={`multi-bet-row-${leg.outcomeId}`}
              />
            </React.Fragment>
          );
        })}
      </BetSpine>
    </ComboGroup>
  );
};
